'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Check, Flame, Users, DollarSign, AlertTriangle, Zap, Star, Copy, CheckCheck, Loader2, CreditCard } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import Icon from '@/components/ui/AppIcon';

// PayPal plan IDs — never exposed as raw links in the UI
const PAYPAL_PLANS = {
  weekly: 'P-1UW264521W3188933NHFOTLY',
  monthly: 'P-22733113UM709481PNHFOVZA',
} as const;

function buildPayPalUrl(planId: string) {
  return `https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=${planId}`;
}

export default function PricingPage() {
  const { lang } = useLanguage();
  const en = lang === 'en';

  // Partner section state
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerResult, setPartnerResult] = useState<{
    referral_link: string;
    exists: boolean;
    display_name: string;
  } | null>(null);
  const [partnerError, setPartnerError] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const handleBecomePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partnerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partnerEmail.trim())) {
      setPartnerError(en ? 'Please enter a valid email address.' : 'Por favor ingresa un correo válido.');
      return;
    }
    setPartnerLoading(true);
    setPartnerError('');
    try {
      const res = await fetch('/api/partners', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: partnerEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPartnerError(data.error || (en ? 'Something went wrong.' : 'Algo salió mal.'));
      } else {
        setPartnerResult(data);
      }
    } catch {
      setPartnerError(en ? 'Network error. Please try again.' : 'Error de red. Inténtalo de nuevo.');
    } finally {
      setPartnerLoading(false);
    }
  };

  const handleCopy = () => {
    if (!partnerResult) return;
    navigator.clipboard.writeText(partnerResult.referral_link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const plans = [
    {
      badge: en ? '🟢 FREE PLAN' : '🟢 PLAN GRATUITO',
      name: en ? 'FREE' : 'GRATIS',
      price: '$0',
      period: '',
      tagline: en ? 'Test the system before upgrading' : 'Prueba el sistema antes de actualizar',
      features: en
        ? [
            '5 signals per 24 hours',
            'Basic access to signal generator',
            'Limited daily usage',
            'No payment required',
          ]
        : [
            '5 señales cada 24 horas',
            'Acceso básico al generador de señales',
            'Uso diario limitado',
            'Sin pago requerido',
          ],
      cta: en ? 'Start Free' : 'Comenzar Gratis',
      ctaHref: '/trading-signals-dashboard',
      paypalPlan: null,
      highlight: false,
      accentColor: 'emerald',
      note: null,
    },
    {
      badge: en ? '🟡 WEEKLY PLAN (TRIAL)' : '🟡 PLAN SEMANAL (PRUEBA)',
      name: en ? 'WEEKLY ACCESS' : 'ACCESO SEMANAL',
      price: '$19',
      period: en ? '/week' : '/semana',
      tagline: en ? 'Perfect to test premium signals' : 'Perfecto para probar señales premium',
      features: en
        ? [
            'Unlimited signals per day',
            'Full access to premium signals',
            'More trading opportunities',
            'Ideal for short-term testing',
          ]
        : [
            'Señales ilimitadas por día',
            'Acceso completo a señales premium',
            'Más oportunidades de trading',
            'Ideal para pruebas a corto plazo',
          ],
      cta: en ? 'Start Trading — $19/week' : 'Comenzar Trading — $19/semana',
      ctaHref: null,
      paypalPlan: 'weekly' as keyof typeof PAYPAL_PLANS,
      highlight: false,
      accentColor: 'amber',
      note: en ? '👉 Most users upgrade after their first week' : '👉 La mayoría actualiza después de su primera semana',
    },
    {
      badge: en ? '🟣 MONTHLY PLAN (BEST VALUE)' : '🟣 PLAN MENSUAL (MEJOR VALOR)',
      name: en ? 'MONTHLY ACCESS' : 'ACCESO MENSUAL',
      price: '$59',
      period: en ? '/month' : '/mes',
      tagline: en ? 'Built for consistent traders' : 'Diseñado para traders consistentes',
      features: en
        ? [
            'Unlimited signals per day',
            'Full premium access',
            'Consistent signal flow',
            'Best cost per week',
            'Priority experience',
          ]
        : [
            'Señales ilimitadas por día',
            'Acceso premium completo',
            'Flujo de señales consistente',
            'Mejor costo por semana',
            'Experiencia prioritaria',
          ],
      cta: en ? 'Unlock Full Access — $59/month (Best Value)' : 'Desbloquear Acceso Completo — $59/mes (Mejor Valor)',
      ctaHref: null,
      paypalPlan: 'monthly' as keyof typeof PAYPAL_PLANS,
      highlight: true,
      accentColor: 'purple',
      note: en
        ? '💰 Save $17/month vs weekly\n👉 Most active users choose this plan' :'💰 Ahorra $17/mes vs semanal\n👉 La mayoría de los usuarios activos eligen este plan',
    },
  ];

  const colorMap: Record<string, { bg: string; border: string; text: string; btn: string; btnHover: string; badge: string }> = {
    emerald: {
      bg: 'bg-emerald-500/5',
      border: 'border-emerald-500/20',
      text: 'text-emerald-400',
      btn: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
      btnHover: 'hover:bg-emerald-500/25',
      badge: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    },
    amber: {
      bg: 'bg-amber-500/5',
      border: 'border-amber-500/20',
      text: 'text-amber-400',
      btn: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
      btnHover: 'hover:bg-amber-500/25',
      badge: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    },
    purple: {
      bg: 'bg-purple-500/8',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      btn: 'bg-purple-500 border-purple-400 text-white',
      btnHover: 'hover:bg-purple-600',
      badge: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    },
  };

  return (
    <div className="min-h-screen bg-[#09090f] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-white/5 bg-[#09090f]/95 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <AppLogo size={32} />
              <div className="flex flex-col">
                <span className="font-semibold text-sm tracking-tight gold-text leading-none">
                  XAU ENGINE AI PRO
                </span>
                <span className="text-[10px] text-zinc-500 font-mono tracking-widest uppercase leading-none mt-0.5">
                  {en ? 'Gold Signal Platform' : 'Plataforma de Señales de Oro'}
                </span>
              </div>
            </div>
            <Link
              href="/trading-signals-dashboard"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-900 border border-white/5 hover:bg-zinc-800 hover:border-white/10 transition-all duration-150 text-xs text-zinc-300 font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {en ? 'Back to Dashboard' : 'Volver al Panel'}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/5 bg-[#0d0d18]">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#d4af37 1px, transparent 1px), linear-gradient(90deg, #d4af37 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
            <Flame className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[11px] font-mono text-amber-400 uppercase tracking-widest">
              {en ? 'Pricing Plans' : 'Planes de Precios'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            {en ? (
              <>Unlock <span className="gold-text">Premium Signals</span></>
            ) : (
              <>Desbloquea <span className="gold-text">Señales Premium</span></>
            )}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl mx-auto">
            {en
              ? "Start free. Upgrade when you're ready to take better entries." :'Comienza gratis. Actualiza cuando estés listo para mejores entradas.'}
          </p>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* Pricing Cards */}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const c = colorMap[plan.accentColor];
              return (
                <div
                  key={plan.name}
                  className={`relative flex flex-col rounded-2xl border p-6 ${plan.highlight ? `${c.bg} ${c.border} ring-1 ring-purple-500/20` : 'bg-[#0d0d18] border-white/5'}`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg">
                        <Star className="w-3 h-3" />
                        {en ? 'Best Value' : 'Mejor Valor'}
                      </div>
                    </div>
                  )}

                  {/* Badge */}
                  <div className={`inline-flex self-start items-center px-2.5 py-1 rounded-full border text-[10px] font-semibold uppercase tracking-widest mb-4 ${c.badge}`}>
                    {plan.badge}
                  </div>

                  {/* Plan name */}
                  <p className="text-[11px] font-mono font-semibold text-zinc-400 uppercase tracking-widest mb-1">{plan.name}</p>

                  {/* Price */}
                  <div className="flex items-end gap-1 mb-1">
                    <span className={`text-4xl font-bold ${c.text}`}>{plan.price}</span>
                    {plan.period && <span className="text-sm text-zinc-500 mb-1.5">{plan.period}</span>}
                  </div>

                  {/* Tagline */}
                  <p className="text-xs text-zinc-400 mb-5">{plan.tagline}</p>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${c.text}`} />
                        <span className="text-xs text-zinc-300 leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Note */}
                  {plan.note && (
                    <div className={`mb-4 p-3 rounded-xl ${c.bg} border ${c.border}`}>
                      {plan.note.split('\n').map((line, i) => (
                        <p key={i} className={`text-[11px] ${c.text} leading-relaxed ${i > 0 ? 'mt-1' : ''}`}>{line}</p>
                      ))}
                    </div>
                  )}

                  {/* CTA */}
                  {plan.paypalPlan ? (
                    <button
                      onClick={() => {
                        window.open(buildPayPalUrl(PAYPAL_PLANS[plan.paypalPlan!]), '_blank', 'noopener,noreferrer');
                      }}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-150 cursor-pointer ${
                        plan.highlight
                          ? 'bg-purple-500 border-purple-400 text-white hover:bg-purple-600 py-3 text-sm'
                          : `${c.btn} ${c.btnHover}`
                      }`}
                    >
                      <CreditCard className={`shrink-0 ${plan.highlight ? 'w-4 h-4' : 'w-3.5 h-3.5'}`} />
                      {plan.cta}
                    </button>
                  ) : (
                    <Link
                      href={plan.ctaHref!}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-150 ${c.btn} ${c.btnHover}`}
                    >
                      <Zap className="w-3.5 h-3.5" />
                      {plan.cta}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          {/* PayPal payment note */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500">
            <CreditCard className="w-3.5 h-3.5 text-zinc-600" />
            <span>
              {en
                ? 'Payments processed securely via PayPal. After subscribing, your access will be activated automatically.' :'Pagos procesados de forma segura a través de PayPal. Después de suscribirte, tu acceso se activará automáticamente.'}
            </span>
          </div>
        </section>

        {/* Trust & Clarity */}
        <section>
          <div className="flex items-start gap-4 p-5 rounded-2xl bg-amber-500/5 border border-amber-500/20">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-400 mb-1">
                {en ? 'Important Notice' : 'Aviso Importante'}
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {en
                  ? 'This platform provides trading signals only. You execute the trades and manage your own risk.'
                  : 'Esta plataforma proporciona señales de trading únicamente. Tú ejecutas las operaciones y gestionas tu propio riesgo.'}
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                {en ? 'For educational purposes only.' : 'Solo con fines educativos.'}
              </p>
            </div>
          </div>
        </section>

        {/* Partner Section */}
        <section>
          <div className="relative overflow-hidden rounded-2xl bg-[#0d0d18] border border-white/5 p-8 sm:p-10">
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#d4af37 1px, transparent 1px), linear-gradient(90deg, #d4af37 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="relative">
              {/* Section header */}
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-amber-400" />
                <span className="text-[11px] font-mono text-amber-400 uppercase tracking-widest font-semibold">
                  {en ? 'Partner Program' : 'Programa de Socios'}
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {en ? (
                  <>Earn by sharing <span className="gold-text">XAU ENGINE AI</span></>
                ) : (
                  <>Gana compartiendo <span className="gold-text">XAU ENGINE AI</span></>
                )}
              </h2>
              <p className="text-sm text-zinc-400 mb-8 max-w-xl">
                {en
                  ? 'Get paid every time someone subscribes using your link.' :'Recibe pagos cada vez que alguien se suscriba usando tu enlace.'}
              </p>

              {/* Commission cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  {
                    icon: Users,
                    color: 'amber',
                    label: en ? '20% Commission' : '20% Comisión',
                    desc: en ? 'Weekly plans' : 'Planes semanales',
                  },
                  {
                    icon: DollarSign,
                    color: 'emerald',
                    label: en ? '30% Recurring' : '30% Recurrente',
                    desc: en ? 'Monthly plans' : 'Planes mensuales',
                  },
                  {
                    icon: Zap,
                    color: 'purple',
                    label: en ? 'Monthly Earnings' : 'Ganancias Mensuales',
                    desc: en ? 'Earn from your referrals' : 'Gana de tus referidos',
                  },
                ].map(({ icon: Icon, color, label, desc }) => {
                  const c = colorMap[color];
                  return (
                    <div key={label} className={`flex items-start gap-3 p-4 rounded-xl ${c.bg} border ${c.border}`}>
                      <div className={`w-8 h-8 rounded-lg ${c.bg} border ${c.border} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-4 h-4 ${c.text}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${c.text}`}>{label}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Partner CTA / Result */}
              {!partnerResult ? (
                <form onSubmit={handleBecomePartner} className="flex flex-col sm:flex-row items-start gap-3 max-w-lg">
                  <input
                    type="email"
                    required
                    value={partnerEmail}
                    onChange={(e) => setPartnerEmail(e.target.value)}
                    placeholder={en ? 'your@email.com' : 'tu@correo.com'}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-900 border border-white/10 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={partnerLoading}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-semibold text-amber-950 whitespace-nowrap"
                  >
                    {partnerLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Users className="w-4 h-4" />
                    )}
                    {partnerLoading
                      ? (en ? 'Generating...' : 'Generando...')
                      : (en ? 'Become a Partner' : 'Conviértete en Socio')}
                  </button>
                  {partnerError && (
                    <p className="text-xs text-red-400 w-full">{partnerError}</p>
                  )}
                </form>
              ) : (
                <div className="flex flex-col gap-3 max-w-lg">
                  {/* Success message */}
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <p className="text-sm font-semibold text-emerald-400">
                      {partnerResult.exists
                        ? (en ? 'You already have a partner link' : 'Ya tienes un enlace de socio')
                        : (en ? 'Your partner link is ready' : 'Tu enlace de socio está listo')}
                    </p>
                  </div>

                  {/* Referral link display */}
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-zinc-900 border border-white/10">
                    <span className="text-xs text-zinc-300 font-mono flex-1 truncate">
                      {partnerResult.referral_link}
                    </span>
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors text-xs font-semibold text-amber-300 shrink-0"
                    >
                      {copied ? (
                        <>
                          <CheckCheck className="w-3.5 h-3.5" />
                          {en ? 'Copied!' : '¡Copiado!'}
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          {en ? 'Copy Link' : 'Copiar Enlace'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
